import { getGroupedPaymentMediumOptions } from "@/lib/billing/payment-medium";
import { clinicClockFor, pickedDay } from "@/lib/clinic-clock";
import { getDefaultDateRange } from "@/lib/date-range-presets";
import { formatToHumanReadable } from "@/lib/format-data";
import { getTimezone } from "@/lib/timezone";

import type { RuleSet } from "@/types/billing";
import type { ChargeFetchParams } from "@/types/charge";
import type { DashboardRequestParams } from "@/types/dashboard";
import type { DepositFilters } from "@/types/deposit";
import { ChargeStatus } from "@/types/enums";
import { InvoiceSetStatus } from "@/types/enums/invoice-set";
import { PaymentStatus } from "@/types/enums/payment";
import { DraftQuoteStatusLabels, QuoteStatus } from "@/types/enums/quote";
import type { InvoiceFilters } from "@/types/invoice";
import type { MerchantAccount } from "@/types/merchant-account";
import type { PaymentFetchParams, PaymentMedium } from "@/types/payment";
import type { ShopOrderFilters } from "@/types/shop-orders";

import {
  LOCATION_SCOPE_DEFAULT,
  dateRangeFilter,
  defineFilters,
  enumSetFilter,
  idSetFilter,
  numberFilter,
  serializeFilterQuerySegments,
  setFilter,
  textFilter,
  tupleDateRangeFilter,
  tupleNumberRangeFilter,
} from "@/features/table-filters/contract";
import type { FilterKey } from "@/features/table-filters/contract";

function tableStateDefinitions<Request extends object>(
  urlPolicy: "none" | "scalar" = "none",
) {
  return [
    numberFilter<Request>({
      key: "page" as FilterKey<Request>,
      label: "Page",
      presented: false,
      urlPolicy,
      queryKey: "page",
    }),
    numberFilter<Request>({
      key: "perPage" as FilterKey<Request>,
      label: "Per page",
      presented: false,
      urlPolicy,
      queryKey: "per_page",
    }),
    textFilter<Request>({
      key: "sortBy" as FilterKey<Request>,
      label: "Sort by",
      presented: false,
      urlPolicy,
      queryKey: "sort_by",
    }),
    textFilter<Request>({
      key: "sortDirection" as FilterKey<Request>,
      label: "Sort direction",
      presented: false,
      urlPolicy,
      queryKey: "sort_direction",
    }),
    textFilter<Request>({
      key: "tz" as FilterKey<Request>,
      label: "Timezone",
      presented: false,
      urlPolicy: "none",
      queryKey: "tz",
    }),
  ];
}

export const invoiceFilterDefinitions = (ruleSets: RuleSet[] = []) =>
  defineFilters<InvoiceFilters>()([
    ...tableStateDefinitions<InvoiceFilters>("scalar"),
    textFilter<InvoiceFilters>({
      key: "searchTerm",
      label: "Search",
      operator: "contains",
      presented: false,
      queryKey: "search_term",
    }),
    enumSetFilter<InvoiceFilters>({
      key: "status",
      label: "Status",
      options: Object.values(InvoiceSetStatus).map((status) => ({
        value: status,
        label: formatToHumanReadable(status),
      })),
    }),
    tupleNumberRangeFilter<InvoiceFilters>({
      key: "amount",
      label: "Amount",
      queryKey: "amount",
    }),
    tupleDateRangeFilter<InvoiceFilters>({
      key: "createdDate",
      label: "Invoice Created",
      queryKey: "created_date",
    }),
    tupleDateRangeFilter<InvoiceFilters>({
      key: "sendTime",
      label: "Send Time",
      queryKey: "send_time",
    }),
    setFilter<InvoiceFilters>({
      key: "ruleSetIds",
      label: "Rule Set",
      queryKey: "rule_set_ids",
      options: ruleSets.map((ruleSet) => ({
        value: ruleSet.id,
        label: ruleSet.name,
      })),
    }),
  ]);

export interface BillingDashboardFilters {
  startDate: string;
  endDate: string;
  locationIds?: string[];
  comment?: string;
}

export const billingDashboardFilterDefinitions =
  defineFilters<BillingDashboardFilters>()([
    dateRangeFilter<BillingDashboardFilters>({
      startKey: "startDate",
      endKey: "endDate",
      label: "Date Range",
      startQueryKey: "start_date",
      endQueryKey: "end_date",
    }),
    setFilter<BillingDashboardFilters>({
      key: "locationIds",
      label: "Location",
      control: "location",
      queryKey: "location_ids",
    }),
    textFilter<BillingDashboardFilters>({
      key: "comment",
      label: "Comment",
      operator: "contains",
    }),
  ]);

export function getDefaultBillingDashboardFilters(
  timeZone: string = getTimezone(),
): BillingDashboardFilters {
  const today = clinicClockFor(timeZone).today();
  const defaultRange = getDefaultDateRange(timeZone);
  return {
    startDate: defaultRange.from ? pickedDay(defaultRange.from) : today,
    endDate: defaultRange.to ? pickedDay(defaultRange.to) : today,
  };
}

function appendParam(
  params: URLSearchParams,
  key: string,
  value: string | number | undefined | null,
): void {
  if (value === undefined || value === null || value === "") return;
  params.append(key, String(value));
}

export function serializeBillingDashboardParams(
  params: DashboardRequestParams,
): URLSearchParams {
  const query = serializeFilterQuerySegments([
    { definitions: billingDashboardFilterDefinitions, values: params },
  ]);
  appendParam(query, "tz", params.tz);
  appendParam(query, "limit", params.limit);
  appendParam(query, "page", params.page);
  appendParam(query, "per_page", params.perPage);
  appendParam(query, "accounting_basis", params.accountingBasis);
  appendParam(query, "comparison_type", params.comparisonType);
  appendParam(query, "comparison_amount", params.comparisonAmount);
  appendParam(query, "comparison_unit", params.comparisonUnit);
  params.paymentMediums?.forEach((medium) =>
    appendParam(query, "payment_mediums", medium),
  );
  return query;
}

export const chargeFilterDefinitions = defineFilters<ChargeFetchParams>()([
  ...tableStateDefinitions<ChargeFetchParams>(),
  textFilter<ChargeFetchParams>({
    key: "name",
    label: "Name",
    operator: "contains",
  }),
  idSetFilter<ChargeFetchParams>({ key: "id", label: "Charge ID" }),
  textFilter<ChargeFetchParams>({
    key: "patientId",
    label: "Patient ID",
    queryKey: "patient_id",
  }),
  textFilter<ChargeFetchParams>({
    key: "chargeExternalId",
    label: "Charge External ID",
    queryKey: "charge_external_id",
  }),
  textFilter<ChargeFetchParams>({
    key: "patientExternalId",
    label: "Patient External ID",
    queryKey: "patient_external_id",
  }),
  textFilter<ChargeFetchParams>({
    key: "patientMrn",
    label: "Patient MRN",
    operator: "contains",
    queryKey: "patient_mrn",
  }),
  tupleNumberRangeFilter<ChargeFetchParams>({
    key: "total",
    label: "Original Amount",
    queryKey: "total",
  }),
  tupleNumberRangeFilter<ChargeFetchParams>({
    key: "totalOutstanding",
    label: "Amount Outstanding",
    queryKey: "total_outstanding",
  }),
  tupleDateRangeFilter<ChargeFetchParams>({
    key: "createdDate",
    label: "Created Date",
    queryKey: "created_date",
  }),
  tupleDateRangeFilter<ChargeFetchParams>({
    key: "paymentDate",
    label: "Payment Date",
    queryKey: "payment_date",
  }),
  enumSetFilter<ChargeFetchParams>({
    key: "status",
    label: "Status",
    options: Object.values(ChargeStatus).map((status) => ({
      value: status,
      label: formatToHumanReadable(status),
    })),
  }),
  setFilter<ChargeFetchParams>({
    key: "locationIds",
    label: "Location",
    control: "location",
    queryKey: "location_ids",
    defaultPolicy: LOCATION_SCOPE_DEFAULT,
  }),
]);

/**
 * The quote's own status, which the charge table's definitions don't carry.
 * A converted draft is a charge, so it never shows on the Quotes table.
 */
export const quoteStatusFilterDefinitions = defineFilters<ChargeFetchParams>()([
  enumSetFilter<ChargeFetchParams>({
    key: "quoteStatus",
    label: "Status",
    queryKey: "quote_status",
    options: Object.values(QuoteStatus)
      .filter((status) => status !== QuoteStatus.CONVERTED)
      .map((status) => ({
        value: status,
        label: DraftQuoteStatusLabels[status],
      })),
  }),
  textFilter<ChargeFetchParams>({
    key: "quoteStatusGroup",
    label: "Status group",
    presented: false,
    urlPolicy: "none",
    queryKey: "quote_status_group",
  }),
]);

/**
 * Quotes are DRAFT charges, so they share the charge request shape. The
 * charge status is absent on purpose: the quote list pins it to DRAFT and
 * never offers it as a control; the Status control is the quote's status.
 * Location is left unscoped by default so the list keeps showing every
 * location's quotes until staff narrows it.
 */
export const quoteFilterDefinitions = defineFilters<ChargeFetchParams>()([
  ...tableStateDefinitions<ChargeFetchParams>(),
  textFilter<ChargeFetchParams>({
    key: "name",
    label: "Patient Name",
    operator: "contains",
    presented: false,
  }),
  idSetFilter<ChargeFetchParams>({ key: "id", label: "Quote ID" }),
  textFilter<ChargeFetchParams>({
    key: "patientId",
    label: "Patient ID",
    queryKey: "patient_id",
  }),
  tupleNumberRangeFilter<ChargeFetchParams>({
    key: "total",
    label: "Quote Total",
    queryKey: "total",
  }),
  tupleDateRangeFilter<ChargeFetchParams>({
    key: "createdDate",
    label: "Created Date",
    queryKey: "created_date",
  }),
  ...quoteStatusFilterDefinitions,
  setFilter<ChargeFetchParams>({
    key: "locationIds",
    label: "Location",
    control: "location",
    queryKey: "location_ids",
  }),
]);

export const paymentFilterDefinitions = (
  merchantAccounts: MerchantAccount[] = [],
  paymentMediums: PaymentMedium[] = [],
) =>
  defineFilters<PaymentFetchParams>()([
    ...tableStateDefinitions<PaymentFetchParams>(),
    textFilter<PaymentFetchParams>({
      key: "payer",
      label: "Patient Name",
      operator: "contains",
    }),
    textFilter<PaymentFetchParams>({
      key: "creatorName",
      label: "Creator name",
      presented: false,
      queryKey: "creator_name",
    }),
    textFilter<PaymentFetchParams>({
      key: "creatorId",
      label: "Creator",
      control: "provider",
      queryKey: "creator_id",
    }),
    textFilter<PaymentFetchParams>({
      key: "providerId",
      label: "Provider",
      control: "provider",
      queryKey: "provider_id",
    }),
    tupleNumberRangeFilter<PaymentFetchParams>({
      key: "amount",
      label: "Amount",
      queryKey: "amount",
    }),
    idSetFilter<PaymentFetchParams>({ key: "id", label: "Payment ID" }),
    textFilter<PaymentFetchParams>({
      key: "patientId",
      label: "Patient ID",
      queryKey: "patient_id",
    }),
    textFilter<PaymentFetchParams>({
      key: "externalPatientId",
      label: "External ID",
      queryKey: "external_patient_id",
    }),
    textFilter<PaymentFetchParams>({
      key: "patientMrn",
      label: "MRN",
      operator: "contains",
      queryKey: "patient_mrn",
    }),
    enumSetFilter<PaymentFetchParams>({
      key: "payerType",
      label: "Payer Type",
      queryKey: "payer_type",
      options: [
        { value: "insurer", label: "Insurer" },
        { value: "patient", label: "Patient" },
      ],
    }),
    textFilter<PaymentFetchParams>({ key: "last4", label: "Last 4" }),
    textFilter<PaymentFetchParams>({
      key: "comment",
      label: "Comment",
      operator: "contains",
    }),
    setFilter<PaymentFetchParams>({
      key: "paymentMediums",
      label: "Payment Medium",
      queryKey: "payment_mediums",
      options: getGroupedPaymentMediumOptions(paymentMediums),
    }),
    setFilter<PaymentFetchParams>({
      key: "merchantAccountIds",
      label: "Merchant",
      queryKey: "merchant_account_ids",
      options: merchantAccounts.map((account) => ({
        value: account.id,
        label: account.name,
      })),
      presented: merchantAccounts.length > 1,
    }),
    enumSetFilter<PaymentFetchParams>({
      key: "status",
      label: "Status",
      options: Object.values(PaymentStatus).map((status) => ({
        value: status,
        label: formatToHumanReadable(status),
      })),
    }),
    tupleDateRangeFilter<PaymentFetchParams>({
      key: "createdDate",
      label: "Payment Date",
      queryKey: "created_date",
    }),
    enumSetFilter<PaymentFetchParams>({
      key: "tags",
      label: "Tags",
      options: [
        { value: "is_payment_plan", label: "Is Payment Plan?" },
        { value: "refunded", label: "Refunded" },
      ],
    }),
    textFilter<PaymentFetchParams>({
      key: "locationId",
      label: "Location",
      presented: false,
      queryKey: "location_id",
    }),
    setFilter<PaymentFetchParams>({
      key: "locationIds",
      label: "Location",
      control: "location",
      queryKey: "location_ids",
      defaultPolicy: LOCATION_SCOPE_DEFAULT,
    }),
  ]);

const depositStatusOptions = [
  "PROCESSING",
  "IN_REVIEW",
  "SUCCEEDED",
  "FAILED",
].map((status) => ({ value: status, label: formatToHumanReadable(status) }));

export const depositFilterDefinitions = defineFilters<DepositFilters>()([
  ...tableStateDefinitions<DepositFilters>(),
  enumSetFilter<DepositFilters>({
    key: "status",
    label: "Status",
    options: depositStatusOptions,
  }),
  tupleNumberRangeFilter<DepositFilters>({
    key: "amount",
    label: "Amount",
    queryKey: "amount",
  }),
  tupleNumberRangeFilter<DepositFilters>({
    key: "fee",
    label: "Fees",
    queryKey: "fee",
  }),
  tupleNumberRangeFilter<DepositFilters>({
    key: "activityCount",
    label: "Transactions",
    queryKey: "activity_count",
  }),
  tupleDateRangeFilter<DepositFilters>({
    key: "createdDate",
    label: "Created Date",
    queryKey: "created_date",
  }),
  tupleDateRangeFilter<DepositFilters>({
    key: "activityDate",
    label: "Payment Date",
    queryKey: "activity_date",
  }),
  textFilter<DepositFilters>({
    key: "groupBy" as FilterKey<DepositFilters>,
    label: "Group by",
    presented: false,
    urlPolicy: "none",
    queryKey: "group_by",
  }),
  textFilter<DepositFilters>({
    key: "groupKey" as FilterKey<DepositFilters>,
    label: "Group key",
    presented: false,
    urlPolicy: "none",
    queryKey: "group_key",
  }),
]);

export function serializeInvoiceFilters(
  params: InvoiceFilters & { locationId?: string; tz?: string },
): URLSearchParams {
  const query = serializeFilterQuerySegments([
    { definitions: invoiceFilterDefinitions(), values: params },
  ]);
  appendParam(query, "location_id", params.locationId);
  return query;
}

export function serializeChargeFilters(
  params: ChargeFetchParams,
): URLSearchParams {
  return serializeFilterQuerySegments([
    { definitions: chargeFilterDefinitions, values: params },
  ]);
}

export function serializeQuoteFilters(
  params: ChargeFetchParams,
): URLSearchParams {
  return serializeFilterQuerySegments([
    { definitions: chargeFilterDefinitions, values: params },
    { definitions: quoteStatusFilterDefinitions, values: params },
  ]);
}

export function serializePaymentFilters(
  params: PaymentFetchParams,
): URLSearchParams {
  return serializeFilterQuerySegments([
    { definitions: paymentFilterDefinitions(), values: params },
  ]);
}

export function serializeDepositFilters(
  params: DepositFilters,
): URLSearchParams {
  return serializeFilterQuerySegments([
    { definitions: depositFilterDefinitions, values: params },
  ]);
}

export const shopOrderFilterDefinitions = defineFilters<ShopOrderFilters>()([
  ...tableStateDefinitions<ShopOrderFilters>(),
  enumSetFilter<ShopOrderFilters>({
    key: "orderStatus",
    label: "Status",
    options: [
      { value: "OPEN", label: "Open" },
      { value: "FULFILLED", label: "Fulfilled" },
    ],
    queryKey: "order_status",
  }),
  enumSetFilter<ShopOrderFilters>({
    key: "method",
    label: "Ship mode",
    options: [
      { value: "SHIP", label: "Ship" },
      { value: "PICKUP", label: "Pickup" },
    ],
    queryKey: "method",
  }),
  tupleDateRangeFilter<ShopOrderFilters>({
    key: "createdDate",
    label: "Order Date",
    queryKey: "created_date",
  }),
  textFilter<ShopOrderFilters>({
    key: "groupBy" as FilterKey<ShopOrderFilters>,
    label: "Group by",
    presented: false,
    urlPolicy: "none",
    queryKey: "group_by",
  }),
  textFilter<ShopOrderFilters>({
    key: "groupKey" as FilterKey<ShopOrderFilters>,
    label: "Group key",
    presented: false,
    urlPolicy: "none",
    queryKey: "group_key",
  }),
]);

export function serializeShopOrderFilters(
  params: ShopOrderFilters,
): URLSearchParams {
  return serializeFilterQuerySegments([
    { definitions: shopOrderFilterDefinitions, values: params },
  ]);
}
